/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  useAccount,
  useBalance,
  useReadContract,
  useWriteContract,
  useWaitForTransactionReceipt,
  useSwitchChain,
} from "wagmi";
import {
  parseEther,
  formatEther,
  formatUnits,
  parseUnits,
  keccak256,
  toBytes,
} from "viem";
import { useState, useCallback, useEffect } from "react";
import {
  CONTRACTS,
  BRIDGE_CONFIG,
  SUPPORTED_CHAINS,
} from "@/lib/bridge/contracts";
import { mainnet } from "wagmi/chains";
import { wagmiConfig } from "@/constants";
import { TokenConfig } from "@/lib/bridge/tokens";
import { readContract, waitForTransactionReceipt } from "wagmi/actions";
import { usePaxosBridge } from "./usePaxosBridge";
import { bridgeAPI } from "@/lib/bridge/api";

// Import campNetwork from wagmi config
const campNetwork = {
  id: 484,
  name: "Camp Network",
} as const;

export type BridgeDirection = "camp-to-eth" | "eth-to-camp";

interface BridgeParams {
  to: string;
  amount: string;
  gasLimit?: bigint;
  slippage?: bigint;
  token: TokenConfig;
}

interface BridgeState {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: Error | null;
  txHash?: string;
  isApproving?: boolean; // Track USDC approval step
  approvalStep?: "checking" | "approving" | "approved" | "bridging"; // More granular approval tracking
}

// LayerZero endpoint IDs
const CAMP_NETWORK_EID = 30381; // LayerZero EID for Camp Network
const ETHEREUM_MAINNET_EID = 30101; // LayerZero EID for Ethereum Mainnet

export function useBridge(selectedToken?: TokenConfig) {
  const { address, chain } = useAccount();
  const { switchChain } = useSwitchChain();
  const {
    writeContractAsync,
    data: contractWriteData,
    error: contractWriteError,
    isPending: contractWritePending,
  } = useWriteContract();
  const [bridgeState, setBridgeState] = useState<BridgeState>({
    isLoading: false,
    isSuccess: false,
    isError: false,
    error: null,
  });

  // Initialize Paxos bridge (keeping for potential future use)
  const paxosBridge = usePaxosBridge();

  // Get native CAMP balance on Camp Network (for camp-to-eth bridging)
  const { data: campNetworkNativeBalance } = useBalance({
    address: address,
    chainId: campNetwork.id,
    query: {
      enabled: !!address,
      refetchInterval: 5000, // Refetch every 5 seconds
    },
  });

  // Get current chain native balance (for when user is connected to the chain)
  const { data: currentChainBalance } = useBalance({
    address: address,
    chainId: chain?.id,
    query: {
      enabled: !!address && !!chain,
      refetchInterval: 5000, // Refetch every 5 seconds
    },
  });

  // Get native ETH balance on Ethereum (for ETH eth-to-camp bridging)
  const { data: ethereumNativeBalance, error: ethBalanceError } = useBalance({
    address: address,
    chainId: mainnet.id,
    query: {
      enabled: !!address,
      refetchInterval: 5000, // Refetch every 5 seconds
    },
  });

  // Debug logging for Ethereum balance issues
  useEffect(() => {
    if (ethBalanceError) {
      console.error("🚨 Ethereum ETH balance fetch error:", ethBalanceError);
    }
    if (address && ethereumNativeBalance) {
      console.log("✅ Ethereum ETH balance fetched successfully:", {
        address,
        balance: formatEther(ethereumNativeBalance.value),
        rpc: "Fallback (LlamaRPC → dRPC → QuickNode)",
      });
    }
  }, [address, ethereumNativeBalance, ethBalanceError]);

  // Get CAMP OFT token balance on Ethereum (for CAMP eth-to-camp bridging)
  const { data: campTokenBalance } = useReadContract({
    address: CONTRACTS.CAMP_OFT.address,
    abi: CONTRACTS.CAMP_OFT.abi,
    functionName: "balanceOf",
    args: address ? [address] : undefined,
    chainId: mainnet.id,
    query: {
      enabled: !!address,
      refetchInterval: 5000, // Refetch every 5 seconds
    },
  });

  // Get ETH OFT token balance on Camp Network (for ETH camp-to-eth bridging)
  const { data: ethOftTokenBalance } = useReadContract({
    address: CONTRACTS.ETH_OFT_TOKEN.address,
    abi: CONTRACTS.ETH_OFT_TOKEN.abi,
    functionName: "balanceOf",
    args: address ? [address] : undefined,
    chainId: campNetwork.id,
    query: {
      enabled: !!address,
      refetchInterval: 5000, // Refetch every 5 seconds
    },
  });

  // Get USDC ERC20 token balance on Ethereum (for USDC eth-to-camp bridging)
  const { data: usdcErc20TokenBalance, error: usdcBalanceError } = useReadContract({
    address: CONTRACTS.USDC_ERC20_TOKEN.address,
    abi: CONTRACTS.USDC_ERC20_TOKEN.abi,
    functionName: "balanceOf",
    args: address ? [address] : undefined,
    chainId: mainnet.id,
    query: {
      enabled: !!address,
      refetchInterval: 5000, // Refetch every 5 seconds
    },
  });

  // Debug logging for USDC balance issues
  useEffect(() => {
    if (usdcBalanceError) {
      console.error("🚨 Ethereum USDC balance fetch error:", usdcBalanceError);
    }
    if (address && usdcErc20TokenBalance) {
      console.log("✅ Ethereum USDC balance fetched successfully:", {
        address,
        balance: formatUnits(usdcErc20TokenBalance, 6),
        contract: CONTRACTS.USDC_ERC20_TOKEN.address,
        rpc: "Fallback (LlamaRPC → dRPC → QuickNode)",
      });
    }
  }, [address, usdcErc20TokenBalance, usdcBalanceError]);

  // Get USDC OFT token balance on Camp Network (for USDC camp-to-eth bridging)
  const { data: usdcOftTokenBalance } = useReadContract({
    address: CONTRACTS.USDC_OFT_TOKEN.address,
    abi: CONTRACTS.USDC_OFT_TOKEN.abi,
    functionName: "balanceOf",
    args: address ? [address] : undefined,
    chainId: campNetwork.id,
    query: {
      enabled: !!address,
      refetchInterval: 5000, // Refetch every 5 seconds
    },
  });

  // Check if user is whitelisted on Camp Network
  const { data: isWhitelisted } = useReadContract({
    address: CONTRACTS.CAMP_TIMELOCK_ESCROW.address,
    abi: CONTRACTS.CAMP_TIMELOCK_ESCROW.abi,
    functionName: "whitelist",
    args: address ? [address] : undefined,
    chainId: campNetwork.id,
    query: {
      enabled: !!address,
    },
  });

  // Helper function to convert address to bytes32
  const addressToBytes32 = useCallback((addr: string) => {
    return `0x000000000000000000000000${addr
      .slice(2)
      .toLowerCase()}` as `0x${string}`;
  }, []);

  const bridgeOut = useCallback(
    async (params: BridgeParams) => {
      if (!address || !chain) {
        throw new Error("Wallet not connected");
      }

      setBridgeState({
        isLoading: true,
        isSuccess: false,
        isError: false,
        error: null,
        isApproving: false,
        approvalStep: undefined,
      });

      try {
        // Ensure we're on Camp Network for bridging out
        if (chain.id !== campNetwork.id) {
          await switchChain({ chainId: campNetwork.id });
        }

        // Parse amount with correct decimals for the token
        // NOTE: USDC OFT on Camp Network uses 18 decimals internally, not 6
        const amountWei =
          params.token.id === "usdc"
            ? parseEther(params.amount) // Use 18 decimals for USDC OFT
            : parseEther(params.amount);
        const gasLimit = params.gasLimit || BRIDGE_CONFIG.DEFAULT_GAS_LIMIT;
        const slippage = params.slippage || BRIDGE_CONFIG.DEFAULT_SLIPPAGE;

        // Validate slippage
        if (slippage >= BRIDGE_CONFIG.MAX_SLIPPAGE) {
          throw new Error("Slippage too high");
        }

        let txHash: string;

        if (params.token.id === "camp") {
          // For CAMP tokens, use the existing CAMP_TIMELOCK_ESCROW contract
          // Enhanced whitelist-based fee calculation
          if (isWhitelisted) {
            // Whitelisted users: LayerZero fees + dust-adjusted amount (immediate bridging)
            // Calculate minimum amount after slippage
            const minAmountWei = amountWei - (amountWei * slippage) / 10000n;

            // Convert gasLimit to the format expected by the contract
            const gasLimitUint128 = gasLimit;

            // Get accurate fee quote from the native OFT adapter
            let nativeFee: bigint;
            let totalValue: bigint;
            try {
              // Create send parameters for the native OFT adapter's quoteSend
              const sendParam = {
                dstEid: ETHEREUM_MAINNET_EID,
                to: addressToBytes32(params.to),
                amountLD: amountWei,
                minAmountLD: minAmountWei,
                extraOptions: `0x000301001101000000000000000000000000${gasLimit
                  .toString(16)
                  .padStart(8, "0")}`,
                composeMsg: "0x" as `0x${string}`,
                oftCmd: "0x" as `0x${string}`,
              };

              // Quote LayerZero fees from the native OFT adapter
              const quoteResult = await readContract(wagmiConfig, {
                address: CONTRACTS.CAMP_NATIVE_OFT_ADAPTER.address,
                abi: CONTRACTS.CAMP_NATIVE_OFT_ADAPTER.abi,
                functionName: "quoteSend",
                args: [sendParam, false],
                chainId: campNetwork.id,
              } as Parameters<typeof readContract>[1]);

              const feeResult = quoteResult as {
                nativeFee: bigint;
                lzTokenFee: bigint;
              };
              nativeFee = feeResult.nativeFee;

              // For whitelisted users: total value = CAMP amount + LayerZero fees
              // The contract will internally handle dust removal from the amount
              totalValue = amountWei + nativeFee;
            } catch (error) {
              console.warn(
                "Failed to get exact fees, using estimation:",
                error,
              );
              // Fallback estimation: ~0.5% of amount for fees
              nativeFee = (amountWei * 5n) / 1000n; // 0.5% fee estimation
              totalValue = amountWei + nativeFee;
            }

            console.log("CAMP bridge params (whitelisted):", {
              to: params.to,
              gasLimit: gasLimitUint128.toString(),
              originalAmount: amountWei.toString(),
              minAmount: minAmountWei.toString(),
              layerZeroFee: nativeFee.toString(),
              totalValue: totalValue.toString(),
            });

            txHash = await writeContractAsync({
              address: CONTRACTS.CAMP_TIMELOCK_ESCROW.address,
              abi: CONTRACTS.CAMP_TIMELOCK_ESCROW.abi,
              functionName: "bridgeOut",
              args: [
                params.to as `0x${string}`, // _to
                gasLimitUint128, // _gaslimit (uint128)
                amountWei, // _originalAmount
                minAmountWei, // _minAmount (calculated with slippage)
                params.to as `0x${string}`, // _refundAddress
              ],
              value: totalValue, // CAMP amount + LayerZero fees for whitelisted users
            });
          } else {
            // Non-whitelisted users: Use bridgeOut with escrow (7-day hold)
            // Calculate minimum amount after slippage
            const minAmountWei = amountWei - (amountWei * slippage) / 10000n;

            // Convert gasLimit to the format expected by the contract
            const gasLimitUint128 = gasLimit;

            // Non-whitelisted users only pay the CAMP amount, no fees upfront
            console.log("CAMP bridge params (non-whitelisted):", {
              to: params.to,
              gasLimit: gasLimitUint128.toString(),
              originalAmount: amountWei.toString(),
              minAmount: minAmountWei.toString(),
              totalValue: amountWei.toString(),
            });

            txHash = await writeContractAsync({
              address: CONTRACTS.CAMP_TIMELOCK_ESCROW.address,
              abi: CONTRACTS.CAMP_TIMELOCK_ESCROW.abi,
              functionName: "bridgeOut",
              args: [
                params.to as `0x${string}`, // _to
                gasLimitUint128, // _gaslimit (uint128)
                amountWei, // _originalAmount
                minAmountWei, // _minAmount (calculated with slippage)
                params.to as `0x${string}`, // _refundAddress
              ],
              value: amountWei, // Non-whitelisted only pay the bridge amount, LayerZero fee paid on release
            });
          }
        } else if (params.token.id === "eth") {
          // For ETH OFT tokens, we need to implement the OFT bridge out logic
          // This will bridge ETH OFT tokens from Camp back to native ETH on Ethereum
          // Calculate min amount with slippage
          const minAmountWei = amountWei - (amountWei * slippage) / 10000n;

          // Create proper LayerZero options
          const gasLimitHex = gasLimit.toString(16).padStart(8, "0");
          const lzOptions =
            `0x000301001101000000000000000000000000${gasLimitHex}` as `0x${string}`;

          // Create SendParam struct for ETH OFT bridging
          const sendParam = {
            dstEid: ETHEREUM_MAINNET_EID, // Destination is Ethereum
            to: addressToBytes32(params.to),
            amountLD: amountWei,
            minAmountLD: minAmountWei,
            extraOptions: lzOptions,
            composeMsg: "0x" as `0x${string}`,
            oftCmd: "0x" as `0x${string}`,
          };

          // Use quoteSend to get the actual LayerZero fees for ETH OFT
          const quoteResult = await readContract(wagmiConfig, {
            address: CONTRACTS.ETH_OFT_TOKEN.address,
            abi: CONTRACTS.ETH_OFT_TOKEN.abi,
            functionName: "quoteSend",
            args: [sendParam, false], // false = pay in native token
            chainId: campNetwork.id,
          } as Parameters<typeof readContract>[1]);

          if (!quoteResult) {
            throw new Error("Failed to quote LayerZero fees for ETH bridge");
          }

          const { nativeFee, lzTokenFee } = quoteResult as {
            nativeFee: bigint;
            lzTokenFee: bigint;
          };

          // Execute the ETH OFT bridge transaction
          txHash = await writeContractAsync({
            address: CONTRACTS.ETH_OFT_TOKEN.address,
            abi: CONTRACTS.ETH_OFT_TOKEN.abi,
            functionName: "send",
            args: [
              sendParam,
              { nativeFee, lzTokenFee },
              address, // refund address
            ],
            value: nativeFee, // Use the actual quoted fee
          });
        } else if (params.token.id === "usdc") {
          // For USDC OFT tokens, bridge from Camp to Ethereum
          // USDC OFT on Camp uses 18 decimals internally
          const minAmountWei = amountWei - (amountWei * slippage) / 10000n;

          // Create proper LayerZero options
          const gasLimitHex = gasLimit.toString(16).padStart(8, "0");
          const lzOptions =
            `0x000301001101000000000000000000000000${gasLimitHex}` as `0x${string}`;

          // Create SendParam struct for USDC OFT bridging
          const sendParam = {
            dstEid: ETHEREUM_MAINNET_EID, // Destination is Ethereum
            to: addressToBytes32(params.to),
            amountLD: amountWei,
            minAmountLD: minAmountWei,
            extraOptions: lzOptions,
            composeMsg: "0x" as `0x${string}`,
            oftCmd: "0x" as `0x${string}`,
          };

          // Use quoteSend to get the actual LayerZero fees for USDC OFT
          const quoteResult = await readContract(wagmiConfig, {
            address: CONTRACTS.USDC_OFT_TOKEN.address,
            abi: CONTRACTS.USDC_OFT_TOKEN.abi,
            functionName: "quoteSend",
            args: [sendParam, false], // false = pay in native token
            chainId: campNetwork.id,
          } as Parameters<typeof readContract>[1]);

          if (!quoteResult) {
            throw new Error("Failed to quote LayerZero fees for USDC bridge");
          }

          const { nativeFee, lzTokenFee } = quoteResult as {
            nativeFee: bigint;
            lzTokenFee: bigint;
          };

          // Execute the USDC OFT bridge transaction
          txHash = await writeContractAsync({
            address: CONTRACTS.USDC_OFT_TOKEN.address,
            abi: CONTRACTS.USDC_OFT_TOKEN.abi,
            functionName: "send",
            args: [
              sendParam,
              { nativeFee, lzTokenFee },
              address, // refund address
            ],
            value: nativeFee, // Use the actual quoted fee
          });
        } else {
          throw new Error(
            `Bridging not supported for token: ${params.token.symbol}`,
          );
        }

        setBridgeState({
          isLoading: false,
          isSuccess: true,
          isError: false,
          error: null,
          txHash,
          isApproving: false,
          approvalStep: undefined,
        });

        return txHash;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error
            : new Error("Bridge transaction failed");
        setBridgeState({
          isLoading: false,
          isSuccess: false,
          isError: true,
          error: errorMessage,
          isApproving: false,
          approvalStep: undefined,
        });
        throw errorMessage;
      }
    },
    [
      address,
      chain,
      switchChain,
      writeContractAsync,
      addressToBytes32,
      isWhitelisted,
    ],
  );

  const bridgeBack = useCallback(
    async (params: BridgeParams) => {
      if (!address || !chain) {
        throw new Error("Wallet not connected");
      }

      // Block bridging back (eth-to-camp) for USDC and ETH (one-way tokens)
      if (params.token.id === "usdc" || params.token.id === "eth") {
        throw new Error(
          `${params.token.symbol} can only be bridged from Camp Network to Ethereum (one-way only).`,
        );
      }

      setBridgeState({
        isLoading: true,
        isSuccess: false,
        isError: false,
        error: null,
        isApproving: false,
        approvalStep: undefined,
      });

      try {
        // Ensure we're on Ethereum Mainnet for bridging back
        if (chain.id !== mainnet.id) {
          await switchChain({ chainId: mainnet.id });
        }

        // Parse amount with correct decimals for the token
        const amountWei =
          params.token.id === "usdc"
            ? parseUnits(params.amount, params.token.decimals)
            : parseEther(params.amount);
        const gasLimit = params.gasLimit || BRIDGE_CONFIG.DEFAULT_GAS_LIMIT;
        const slippage = params.slippage || BRIDGE_CONFIG.DEFAULT_SLIPPAGE;

        // Calculate min amount with slippage
        const minAmountWei = amountWei - (amountWei * slippage) / 10000n;

        // Convert gasLimit to hex and pad to 8 characters (4 bytes)
        const gasLimitHex = gasLimit.toString(16).padStart(8, "0");
        const lzOptions =
          `0x000301001101000000000000000000000000${gasLimitHex}` as `0x${string}`;

        let txHash: string;

        if (params.token.id === "camp") {
          // For CAMP tokens, use the existing CAMP_OFT contract
          const sendParam = {
            dstEid: CAMP_NETWORK_EID, // Destination is Camp Network
            to: addressToBytes32(params.to),
            amountLD: amountWei,
            minAmountLD: minAmountWei,
            extraOptions: lzOptions,
            composeMsg: "0x" as `0x${string}`,
            oftCmd: "0x" as `0x${string}`,
          };

          // Use quoteSend to get the actual LayerZero fees
          const quoteResult = await readContract(wagmiConfig, {
            address: CONTRACTS.CAMP_OFT.address,
            abi: CONTRACTS.CAMP_OFT.abi,
            functionName: "quoteSend",
            args: [sendParam, false], // false = pay in native token
            chainId: mainnet.id,
          } as Parameters<typeof readContract>[1]);

          if (!quoteResult) {
            throw new Error("Failed to quote LayerZero fees");
          }

          const { nativeFee, lzTokenFee } = quoteResult as {
            nativeFee: bigint;
            lzTokenFee: bigint;
          };

          // Execute the CAMP bridge transaction
          txHash = await writeContractAsync({
            address: CONTRACTS.CAMP_OFT.address,
            abi: CONTRACTS.CAMP_OFT.abi,
            functionName: "send",
            args: [
              sendParam,
              { nativeFee, lzTokenFee },
              address, // refund address
            ],
            value: nativeFee, // Use the actual quoted fee
          });
        } else if (params.token.id === "eth") {
          // For ETH bridging, use the GenericNativeOFTAdapter
          // This bridges native ETH from Ethereum to ETH OFT on Camp
          const sendParam = {
            dstEid: CAMP_NETWORK_EID, // Destination is Camp Network
            to: addressToBytes32(params.to),
            amountLD: amountWei,
            minAmountLD: minAmountWei,
            extraOptions: lzOptions,
            composeMsg: "0x" as `0x${string}`,
            oftCmd: "0x" as `0x${string}`,
          };

          // Use quoteSend to get the actual LayerZero fees for ETH native adapter
          const quoteResult = await readContract(wagmiConfig, {
            address: CONTRACTS.ETH_NATIVE_OFT_ADAPTER.address,
            abi: CONTRACTS.ETH_NATIVE_OFT_ADAPTER.abi,
            functionName: "quoteSend",
            args: [sendParam, false], // false = pay in native token
            chainId: mainnet.id,
          } as Parameters<typeof readContract>[1]);

          if (!quoteResult) {
            throw new Error("Failed to quote LayerZero fees for ETH bridge");
          }

          const { nativeFee, lzTokenFee } = quoteResult as {
            nativeFee: bigint;
            lzTokenFee: bigint;
          };
          const totalValue = amountWei + nativeFee; // amount + LayerZero fees

          // Execute the ETH bridge transaction using the native OFT adapter
          txHash = await writeContractAsync({
            address: CONTRACTS.ETH_NATIVE_OFT_ADAPTER.address,
            abi: CONTRACTS.ETH_NATIVE_OFT_ADAPTER.abi,
            functionName: "send",
            args: [
              sendParam,
              { nativeFee, lzTokenFee },
              address, // refund address
            ],
            value: totalValue, // Native ETH amount + fees
          });
        } else if (params.token.id === "usdc") {
          // For USDC bridging, use the USDC OFT Adapter on Ethereum
          // First check and approve USDC allowance if needed
          const usdcContract = CONTRACTS.USDC_ERC20_TOKEN.address;
          const adapterContract = CONTRACTS.USDC_OFT_ADAPTER.address;

          // Check current allowance
          const currentAllowance = await readContract(wagmiConfig, {
            address: usdcContract,
            abi: CONTRACTS.USDC_ERC20_TOKEN.abi,
            functionName: "allowance",
            args: [address, adapterContract],
            chainId: mainnet.id,
          } as Parameters<typeof readContract>[1]);

          // Approve if needed
          if ((currentAllowance as bigint) < amountWei) {
            setBridgeState((prev) => ({
              ...prev,
              isApproving: true,
              approvalStep: "approving",
            }));

            await writeContractAsync({
              address: usdcContract,
              abi: CONTRACTS.USDC_ERC20_TOKEN.abi,
              functionName: "approve",
              args: [adapterContract, amountWei],
            });

            setBridgeState((prev) => ({
              ...prev,
              approvalStep: "approved",
            }));
          }

          const sendParam = {
            dstEid: CAMP_NETWORK_EID, // Destination is Camp Network
            to: addressToBytes32(params.to),
            amountLD: amountWei,
            minAmountLD: minAmountWei,
            extraOptions: lzOptions,
            composeMsg: "0x" as `0x${string}`,
            oftCmd: "0x" as `0x${string}`,
          };

          // Use quoteSend to get the actual LayerZero fees for USDC adapter
          const quoteResult = await readContract(wagmiConfig, {
            address: CONTRACTS.USDC_OFT_ADAPTER.address,
            abi: CONTRACTS.USDC_OFT_ADAPTER.abi,
            functionName: "quoteSend",
            args: [sendParam, false], // false = pay in native token
            chainId: mainnet.id,
          } as Parameters<typeof readContract>[1]);

          if (!quoteResult) {
            throw new Error("Failed to quote LayerZero fees for USDC bridge");
          }

          const { nativeFee, lzTokenFee } = quoteResult as {
            nativeFee: bigint;
            lzTokenFee: bigint;
          };

          setBridgeState((prev) => ({
            ...prev,
            approvalStep: "bridging",
          }));

          // Execute the USDC bridge transaction using the OFT adapter
          txHash = await writeContractAsync({
            address: CONTRACTS.USDC_OFT_ADAPTER.address,
            abi: CONTRACTS.USDC_OFT_ADAPTER.abi,
            functionName: "send",
            args: [
              sendParam,
              { nativeFee, lzTokenFee },
              address, // refund address
            ],
            value: nativeFee, // Only LayerZero fees, no USDC amount
          });
        } else {
          throw new Error(
            `Bridging not supported for token: ${params.token.symbol}`,
          );
        }

        setBridgeState({
          isLoading: false,
          isSuccess: true,
          isError: false,
          error: null,
          txHash,
          isApproving: false,
          approvalStep: undefined,
        });

        return txHash;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error
            : new Error("Bridge back transaction failed");
        setBridgeState({
          isLoading: false,
          isSuccess: false,
          isError: true,
          error: errorMessage,
          isApproving: false,
          approvalStep: undefined,
        });
        throw errorMessage;
      }
    },
    [address, chain, switchChain, writeContractAsync, addressToBytes32],
  );

  // Main bridge function that routes to the correct implementation
  const bridge = useCallback(
    async (direction: BridgeDirection, params: BridgeParams) => {
      // All tokens use LayerZero OFT bridging
      console.log("🔄 Routing bridging to LayerZero");
      if (direction === "camp-to-eth") {
        return bridgeOut(params);
      } else {
        return bridgeBack(params);
      }
    },
    [bridgeOut, bridgeBack],
  );

  const getBalance = useCallback(
    (direction: BridgeDirection, token?: TokenConfig) => {
      const tokenToUse = token || selectedToken;
      if (!tokenToUse) return "0";

      if (direction === "camp-to-eth") {
        // Bridging from Camp to Ethereum
        if (tokenToUse.id === "camp") {
          // For CAMP tokens, return native CAMP balance on Camp Network
          const balanceToUse =
            chain?.id === campNetwork.id
              ? currentChainBalance
              : campNetworkNativeBalance;
          return balanceToUse ? formatEther(balanceToUse.value) : "0";
        } else if (tokenToUse.id === "eth") {
          // For ETH tokens, return ETH OFT balance on Camp Network
          return ethOftTokenBalance ? formatEther(ethOftTokenBalance) : "0";
        } else if (tokenToUse.id === "usdc") {
          // For USDC tokens, return USDC OFT balance on Camp Network
          // IMPORTANT: USDC OFT balance is stored with 18 decimals, not 6
          return usdcOftTokenBalance ? formatEther(usdcOftTokenBalance) : "0";
        }
      } else {
        // Bridging from Ethereum to Camp
        if (tokenToUse.id === "camp") {
          // For CAMP tokens, return CAMP OFT token balance on Ethereum
          return campTokenBalance ? formatEther(campTokenBalance) : "0";
        } else if (tokenToUse.id === "eth") {
          // For ETH tokens, return native ETH balance on Ethereum
          return ethereumNativeBalance
            ? formatEther(ethereumNativeBalance.value)
            : "0";
        } else if (tokenToUse.id === "usdc") {
          // For USDC tokens, return USDC ERC20 balance on Ethereum
          // Native USDC on Ethereum should have actual 6 decimals
          return usdcErc20TokenBalance
            ? formatUnits(usdcErc20TokenBalance, tokenToUse.decimals)
            : "0";
        }
      }

      return "0";
    },
    [
      selectedToken,
      campNetworkNativeBalance,
      currentChainBalance,
      campTokenBalance,
      ethereumNativeBalance,
      ethOftTokenBalance,
      usdcErc20TokenBalance,
      usdcOftTokenBalance,
      chain?.id,
    ],
  );

  const getBalanceFormatted = useCallback(
    (direction: BridgeDirection, token?: TokenConfig) => {
      const balance = getBalance(direction, token);
      const parsed = parseFloat(balance);

      return parsed > 0
        ? parsed.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 6,
          })
        : "0.00";
    },
    [getBalance],
  );

  const getBalanceSymbol = useCallback(
    (direction: BridgeDirection, token?: TokenConfig) => {
      const tokenToUse = token || selectedToken;
      return tokenToUse?.symbol || "CAMP";
    },
    [selectedToken],
  );

  const canBridge = useCallback(
    (direction: BridgeDirection, amount: string, token?: TokenConfig) => {
      if (!address || !amount) return false;

      const balance = parseFloat(getBalance(direction, token));
      const bridgeAmount = parseFloat(amount);

      return bridgeAmount > 0 && bridgeAmount <= balance;
    },
    [address, getBalance],
  );

  const getRequiredChain = useCallback((direction: BridgeDirection) => {
    return direction === "camp-to-eth" ? campNetwork : mainnet;
  }, []);

  const isOnCorrectChain = useCallback(
    (direction: BridgeDirection) => {
      const requiredChain = getRequiredChain(direction);
      return chain?.id === requiredChain.id;
    },
    [chain, getRequiredChain],
  );

  // Determine if we should use Paxos state (when dealing with USDC) - keeping for future
  const shouldUsePaxosState = false; // Disabled, using LayerZero for all tokens
  const effectiveBridgeState = shouldUsePaxosState
    ? paxosBridge.bridgeState
    : bridgeState;

  return {
    // State - merge Paxos state for USDC, LayerZero state for others
    bridgeState: {
      ...effectiveBridgeState,
      isLoading: shouldUsePaxosState
        ? effectiveBridgeState.isLoading
        : contractWritePending,
      // Include Paxos-specific states when relevant (disabled for now)
      ...(shouldUsePaxosState
        ? {
            isPaxosBridge: true,
            paxosStep: paxosBridge.bridgeState.paxosStep,
          }
        : {}),
    },
    isWhitelisted: !!isWhitelisted,

    // Balances
    getBalance,
    getBalanceFormatted,
    getBalanceSymbol,

    // Raw balance data for debugging
    campNetworkNativeBalance: campNetworkNativeBalance
      ? formatEther(campNetworkNativeBalance.value)
      : "0",
    currentChainBalance: currentChainBalance
      ? formatEther(currentChainBalance.value)
      : "0",
    campTokenBalance: campTokenBalance ? formatEther(campTokenBalance) : "0",
    ethereumNativeBalance: ethereumNativeBalance
      ? formatEther(ethereumNativeBalance.value)
      : "0",
    ethOftTokenBalance: ethOftTokenBalance
      ? formatEther(ethOftTokenBalance)
      : "0",
    usdcErc20TokenBalance: usdcErc20TokenBalance
      ? formatUnits(usdcErc20TokenBalance, 6)
      : "0",
    usdcOftTokenBalance: usdcOftTokenBalance
      ? formatEther(usdcOftTokenBalance) // USDC OFT uses 18 decimals
      : "0",

    // Actions
    bridgeOut,
    bridgeBack,
    bridge,
    canBridge,

    // Chain helpers - use Paxos helpers for USDC (disabled for now)
    getRequiredChain: shouldUsePaxosState
      ? paxosBridge.getRequiredChain
      : getRequiredChain,
    isOnCorrectChain: shouldUsePaxosState
      ? paxosBridge.isOnCorrectChain
      : isOnCorrectChain,
    switchChain,

    // Current state
    currentChain: chain,
    address,

    // Paxos-specific access (for debugging/advanced usage) - disabled for now
    paxosBridge: shouldUsePaxosState ? paxosBridge : null,
  };
}

// Hook to wait for transaction confirmation
export function useBridgeTransaction(txHash?: string) {
  const {
    data: txReceipt,
    isLoading,
    isSuccess,
    isError,
  } = useWaitForTransactionReceipt({
    hash: txHash as `0x${string}`,
    query: {
      enabled: !!txHash,
    },
  });

  return {
    txReceipt,
    isConfirming: isLoading,
    isConfirmed: isSuccess,
    isError,
  };
}

// Additional functionality for release deposits (manual deposit releases after timelock)
export function useReleaseDeposit() {
  const { address, chain } = useAccount();
  const { switchChain } = useSwitchChain();
  const { writeContractAsync } = useWriteContract();
  const [releaseState, setReleaseState] = useState<BridgeState>({
    isLoading: false,
    isSuccess: false,
    isError: false,
    error: null,
  });

  // Get list of ready deposits for the user from Bridge API
  const [readyDeposits, setReadyDeposits] = useState<string[] | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!address) return;

    const fetchReadyDeposits = async () => {
      try {
        const response = await bridgeAPI.getUserTransactions(address, {
          status: "ready_to_release",
        });

        // Extract depositIds from transactions that are ready for release
        const readyDepositIds = response.data.transactions
          .filter(
            (tx) => tx.overallStatus === "ready_to_release" && tx.depositId,
          )
          .map((tx) => tx.depositId!);

        setReadyDeposits(readyDepositIds);
      } catch (error) {
        console.error("Error fetching ready deposits:", error);
        setReadyDeposits([]);
      }
    };

    fetchReadyDeposits();

    // Refetch every 10 seconds
    const interval = setInterval(fetchReadyDeposits, 10000);
    return () => clearInterval(interval);
  }, [address]);

  const releaseDeposit = useCallback(
    async (depositId: string, gasLimit?: bigint) => {
      if (!address || !chain) {
        throw new Error("Wallet not connected");
      }

      setReleaseState({
        isLoading: true,
        isSuccess: false,
        isError: false,
        error: null,
      });

      try {
        // Ensure we're on Camp Network for releasing deposits
        if (chain.id !== campNetwork.id) {
          await switchChain({ chainId: campNetwork.id });
        }

        const useGasLimit = gasLimit || BRIDGE_CONFIG.DEFAULT_GAS_LIMIT;

        // We need to get the deposit info to calculate the LayerZero fees
        const depositInfo = await readContract(wagmiConfig, {
          address: CONTRACTS.CAMP_TIMELOCK_ESCROW.address,
          abi: CONTRACTS.CAMP_TIMELOCK_ESCROW.abi,
          functionName: "pending",
          args: [BigInt(depositId)],
          chainId: campNetwork.id,
        } as Parameters<typeof readContract>[1]);

        const [amount, minAmount, refundAddress, to] = depositInfo as [
          bigint,
          bigint,
          string,
          string,
          bigint,
        ];

        // Estimate LayerZero fees for the release
        let quotedFees: bigint;
        try {
          const sendParam = {
            dstEid: ETHEREUM_MAINNET_EID,
            to: `0x000000000000000000000000${to.slice(2).toLowerCase()}` as `0x${string}`,
            amountLD: amount,
            minAmountLD: minAmount,
            extraOptions: `0x000301001101000000000000000000000000${useGasLimit
              .toString(16)
              .padStart(8, "0")}`,
            composeMsg: "0x" as `0x${string}`,
            oftCmd: "0x" as `0x${string}`,
          };

          const quoteResult = await readContract(wagmiConfig, {
            address: CONTRACTS.CAMP_NATIVE_OFT_ADAPTER.address,
            abi: CONTRACTS.CAMP_NATIVE_OFT_ADAPTER.abi,
            functionName: "quoteSend",
            args: [sendParam, false],
            chainId: campNetwork.id,
          } as Parameters<typeof readContract>[1]);

          const { nativeFee } = quoteResult as {
            nativeFee: bigint;
            lzTokenFee: bigint;
          };

          quotedFees = nativeFee;
        } catch (error) {
          console.warn("Failed to estimate release fees:", error);
          quotedFees = parseEther("0.005"); // Fallback fee estimation
        }

        console.log("Release deposit params:", {
          depositId: depositId,
          gasLimit: useGasLimit.toString(),
          quotedFees: quotedFees.toString(),
        });

        // Use the release function to release a specific deposit
        const txHash = await writeContractAsync({
          address: CONTRACTS.CAMP_TIMELOCK_ESCROW.address,
          abi: CONTRACTS.CAMP_TIMELOCK_ESCROW.abi,
          functionName: "release",
          args: [
            BigInt(depositId), // _depositId
            BigInt(useGasLimit), // _gaslimit as uint128
          ],
          value: quotedFees, // LayerZero fees for bridging
        });

        setReleaseState({
          isLoading: false,
          isSuccess: true,
          isError: false,
          error: null,
          txHash,
        });

        return txHash;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error
            : new Error("Release deposit transaction failed");
        setReleaseState({
          isLoading: false,
          isSuccess: false,
          isError: true,
          error: errorMessage,
        });
        throw errorMessage;
      }
    },
    [address, chain, switchChain, writeContractAsync],
  );

  return {
    releaseState,
    readyDeposits,
    releaseDeposit,
  };
}
